import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import useWorkerSchedulerData from "../../../controllers/use-worker-scheduler-data";

interface RemoveCallOffDialogProps {
	open: boolean;
	workerFullName: string;
	previousStatusId: string;
	previousStatusName: string;
	onConfirm: () => void;
	onCancel: () => void;
	loading: boolean;
}

const RemoveCallOffDialog: React.FC<RemoveCallOffDialogProps> = ({
	open,
	workerFullName,
	previousStatusId,
	previousStatusName,
	onConfirm,
	onCancel,
	loading,
}) => {
	const { schedulingStatusesObj } = useWorkerSchedulerData();
	const { t } = useTranslation();

	return (
		<Dialog
			open={open}
			onClose={onCancel}
			maxWidth='sm'
			fullWidth
			aria-labelledby='remove-call-off-dialog-title'
			aria-describedby='remove-call-off-dialog-description'
		>
			<DialogTitle
				id='remove-call-off-dialog-title'
				sx={{
					fontFamily: "Roboto",
					fontWeight: 500,
					fontSize: "20px",
					lineHeight: "24px",
					color: "#000000DE",
					padding: "24px 24px 8px 24px",
				}}
			>
				{t("full-time-worker-scheduler_remove-time-off-for", {
					name: workerFullName,
				})}
			</DialogTitle>
			<DialogContent
				sx={{
					padding: "8px 24px 20px 24px",
				}}
			>
				<DialogContentText
					id='remove-call-off-dialog-description'
					sx={{
						fontFamily: "Roboto",
						fontWeight: 400,
						fontSize: "14px",
						lineHeight: "20px",
						letterSpacing: "0.25px",
						color: "#00000099",
						marginBottom: "16px",
					}}
				>
					{t("full-time-worker-scheduler_restore-previous-status-message", {
						status: previousStatusName,
					})}
				</DialogContentText>

				<Box
					sx={{
						backgroundColor: "#F5F5F5",
						padding: "12px 16px",
						borderRadius: "4px",
						border: "1px solid #E0E0E0",
					}}
				>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 500,
							fontSize: "14px",
							lineHeight: "20px",
							color: "#000000DE",
							marginBottom: "4px",
						}}
					>
						{t("full-time-worker-scheduler_restoring-to-status")}
					</Typography>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							backgroundColor:
								schedulingStatusesObj[previousStatusId]?.backgroundColor,
							color: schedulingStatusesObj[previousStatusId]?.textColor,
							padding: "4px 12px",
							borderRadius: "16px",
							fontSize: "12px",
							fontWeight: 500,
							border: `1px solid ${schedulingStatusesObj[previousStatusId]?.borderColor}`,
						}}
					>
						{previousStatusName}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions
				sx={{
					padding: "8px 24px 24px 24px",
					gap: "8px",
				}}
			>
				<Button
					onClick={onCancel}
					variant='outlined'
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "20px",
						letterSpacing: "0.1px",
						textTransform: "uppercase",
						minWidth: "64px",
						padding: "8px 16px",
					}}
				>
					{t("common_cancel")}
				</Button>
				<Button
					onClick={onConfirm}
					variant='contained'
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "14px",
						lineHeight: "20px",
						letterSpacing: "0.1px",
						textTransform: "uppercase",
						minWidth: "120px",
						padding: "8px 16px",
					}}
					disabled={loading}
				>
					{t("full-time-worker-scheduler_confirm-return-to-shift")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default RemoveCallOffDialog;
